import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { UntypedFormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BaseFormComponent } from '@classes/extends';
import { FormValidator } from '@composants/validators';
import { FicheclientService as FicheClientService, Qualite, RFQualite } from '@estel-numerique/galaclick-api';
import { DateHelper, TraductionCoreService, ValidatorPatternService } from '@estel-numerique/galaclick-core';
import { Constantes } from '@services/util/constantes';
import { lastValueFrom } from 'rxjs';
import { DialogGalaComponent } from '../../../../composants/dialog-gala/dialog-gala.component';
import { SelectGala } from '../../../../composants/select/select.component';
import { DateGalaComponent } from '../../../../composants/date-gala/date-gala.component';
import { MatButton } from '@angular/material/button';

export interface FicheClientEngagementsComponentData {
    idClient: number;
    listeQualite: Qualite[];
    qualite?: Qualite
}
@Component({
    templateUrl: 'ficheclient-engagements.component.html',
    imports: [DialogGalaComponent, FormsModule, ReactiveFormsModule, SelectGala, DateGalaComponent, MatButton]
})
export class FicheClientEngagementsComponent extends BaseFormComponent implements OnInit {
    public traductionCore = inject(TraductionCoreService);
    public validPattern = inject(ValidatorPatternService);
    private wsFicheClient = inject(FicheClientService);
    private formBuilder = inject(UntypedFormBuilder);
    private ref = inject(ChangeDetectorRef);
    private dialogRef = inject<MatDialogRef<FicheClientEngagementsComponent>>(MatDialogRef);
    public data = inject<FicheClientEngagementsComponentData>(MAT_DIALOG_DATA);

    public codeModule: string = Constantes.CodeOption.CFICHECLIENT; // Code module/option testé pour vérifier l'accès à cette fonction (si null = OK)

    @Input() public titre: string;
    @Output() saved = new EventEmitter<Qualite>();

    // Traductions
    protected lgTrad: Record<string, string> = {};

    public qualiteEngagement: Qualite = {};
    public listeEngagements: RFQualite[];
    public idClient: number;
    public isSuppression: boolean;

    constructor() {
        super();
        this.lgTrad = {
            AFFENG: this.translate.instant('APPFICHECLIENT.AFFENG'),
            ENGAGEMENTS: this.translate.instant('APPFICHECLIENT.ENGAGEMENTS'),
            DEBUT: this.translate.instant('DEBUT'),
            FIN: this.translate.instant('FIN'),
            BEGINNINGDATE: this.translate.instant('BEGINNINGDATE'),
            ENDDATE: this.translate.instant('ENDDATE'),
            ENR: this.translate.instant('ENR'),
            RET: this.translate.instant('RET'),
        };
        this.formData = this.formBuilder.group({
            RFQUALITE: [null, Validators.required],
            DDEBUT: [null, [Validators.required, FormValidator.validDate]],
            DFIN: [null, [FormValidator.validDate]],
        }, { validators: [this.util.date.compareDate('DDEBUT', 'DFIN')] });
    }
    ngOnInit() {
        super.ngOnInit();
        this.show(this.data.idClient, this.data.listeQualite, this.data.qualite).catch(e => this.util.fErreur(e));
    }

    // =========================================================================
    // CHARGEMENT CONTEXTE
    // =========================================================================

    private async show(idClient: number, listQltClient: Qualite[], qualite?: Qualite) {
        try {
            this.util.showLoader();
            this.idClient = idClient;
            const listeTmp: RFQualite[] = await lastValueFrom(this.wsCacheLow.getRefQualite());

            if (qualite) {
                this.qualiteEngagement = qualite;
                this.listeEngagements = listeTmp;
                this.isSuppression = true;
            } else {
                this.listeEngagements = listeTmp.filter((rf: RFQualite) => {
                    let boOk = true;
                    listQltClient.forEach((qlt: Qualite) => {
                        if (rf.IDRFQUALITE === qlt.RFQUALITE.IDRFQUALITE) {
                            boOk = false;
                        }
                    });
                    return boOk;
                });
                this.isSuppression = false;
            }

            this.formData.markAsPristine();
            this.formData.markAsUntouched();

            this.chargerForm();
        } catch (e) {
            this.util.fErreur(e);
        } finally {
            this.util.hideLoader();
        }
    }

    async enregistrerEngagement() {
        this.util.showLoader();
        try {
            this.validationFormulaire(this.formData);
            this.getResultatFromForm();

            if (this.formData.valid) {

                if (this.isSuppression) {
                    this.qualiteEngagement = await this.wsFicheClient.putEngagementQualite(this.idClient, this.qualiteEngagement).toPromise();
                } else {
                    this.qualiteEngagement = await this.wsFicheClient.postEngagementQualite(this.idClient, this.qualiteEngagement).toPromise();
                }
                this.saved.emit(this.qualiteEngagement);
                this.hideModal();
            }
        } catch (e) {
            this.util.fErreur(e);
        } finally {
            this.util.hideLoader();
        }
    }

    public getResultatFromForm() {
        Object.assign(this.qualiteEngagement, this.formData.getRawValue());
    }

    public chargerForm() {
        if (this.isSuppression) {
            this.ref.detectChanges();

            this.qualiteEngagement.RFQUALITE = this.listeEngagements.find((q: RFQualite) => {
                return this.qualiteEngagement.RFQUALITE.IDRFQUALITE == q.IDRFQUALITE;
            });
            if (!this.qualiteEngagement.DFIN) {
                this.qualiteEngagement.DFIN = DateHelper.getDateJourISO();
            }

            this.formData.reset(this.qualiteEngagement);
            this.formData.markAsUntouched();

            this.formData.get('DDEBUT').setValue(null);
            this.formData.get('DDEBUT').clearValidators();
            this.formData.get('DDEBUT').updateValueAndValidity();
            this.formData.get('DFIN').setValidators([Validators.required]);
        } else {
            this.formData.get('DDEBUT').setValidators([Validators.required]);
            this.formData.get('DFIN').setValue(null);
            this.formData.get('DFIN').clearValidators();
            this.formData.get('DFIN').updateValueAndValidity();
        }
    }



    askHideModal() {
        if (!this.isSuppression && !this.formData.untouched) {
            this.util.fConfirm(this.translate.instant('QUITNOSAVE'), () => {
                this.hideModal();
            }, this.translate.instant('MODIFDONE'));
        } else {
            this.hideModal();
        }
    }

    hideModal() {
        this.resetModalValue();
        this.dialogRef.close();
    }

    resetModalValue() {
        this.qualiteEngagement = {};
        this.formData.reset();
        this.formData.markAsPristine();
        this.formData.markAsUntouched();
    }

}
